import { motion } from 'framer-motion';
import { MemberEarningsCard } from '../components/OnChainMatrixCard';
import { IdReportsPanel, SlotStateTable } from '../components/IdReports';
import { DormancyReminder } from '../components/DormancyReminder';
import { CdtCountryBanner } from '../components/CdtBenefitCard';
import {
  Activity,
  ArrowRight,
  BadgeDollarSign,
  Boxes,
  Clock3,
  Crown,
  ExternalLink,
  Layers3,
  Network,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  WalletCards,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MemberDataState } from '../components/DataState';
import { ReferralCard } from '../components/ReferralCard';
import { formatDate, formatToken, shortenAddress } from '../lib/format';
import { slotLabel } from '../lib/slots';
import { PageHeading, ProgressBar, StatCard, StatusPill } from '../components/Ui';
import { useAppData } from '../state/AppDataContext';

function Sparkline({ chartData }: { chartData: number[] }) {
  const width = 700;
  const height = 220;
  const max = Math.max(...chartData);
  const min = Math.min(...chartData);
  const range = Math.max(1, max - min);
  const points = chartData.map((value, index) => {
    const x = (index / Math.max(1, chartData.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 30) - 15;
    return [x, y] as const;
  });
  const path = points.map(([x, y], index) => `${index ? 'L' : 'M'} ${x} ${y}`).join(' ');
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="spark-chart" role="img" aria-label="Network volume trend over the last fourteen days">
      <defs>
        <linearGradient id="area-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8b69ff" stopOpacity=".45" /><stop offset="1" stopColor="#8b69ff" stopOpacity="0" /></linearGradient>
        <linearGradient id="line-fill" x1="0" y1="0" x2="1" y2="0"><stop stopColor="#55d6ff" /><stop offset=".5" stopColor="#8871ff" /><stop offset="1" stopColor="#e760ff" /></linearGradient>
      </defs>
      {[1, 2, 3, 4].map((line) => <line key={line} x1="0" y1={line * 44} x2={width} y2={line * 44} className="chart-grid-line" />)}
      <motion.path d={area} fill="url(#area-fill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} />
      <motion.path d={path} fill="none" stroke="url(#line-fill)" strokeWidth="4" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease: 'easeOut' }} />
      {points.map(([x, y], index) => index === points.length - 1 ? <circle key={index} cx={x} cy={y} r="6" className="chart-last-dot" /> : null)}
    </svg>
  );
}

export function OverviewPage() {
  const data = useAppData();
  const member = data.member;
  if (!member) return <MemberDataState mode={data.mode} error={data.error} onRetry={data.refresh} />;
  const activeSlots = data.slots.filter((slot) => slot.active).length;
  const currentSlot = data.slots.find((slot) => slot.number === member.activeSlot) ?? data.slots.find((slot) => slot.active);
  const healthyRpcs = data.rpcProviders.filter((provider) => provider.state === 'healthy').length;
  return (
    <>
      <PageHeading
        eyebrow="MEMBER COMMAND CENTER"
        title={`Good evening, ${member.id}`}
        description="Your network, positions, utility and node health—reconciled from the chain."
        actions={<><StatusPill tone={data.mode === 'authenticated' ? 'green' : data.mode === 'stale' ? 'amber' : 'demo'}><Sparkles size={13} /> {data.mode === 'authenticated' ? 'On-chain data' : data.mode === 'stale' ? 'Stale on-chain data' : 'Development fixture'}</StatusPill><Link className="button primary" to="/join"><Zap size={16} /> Join another ID</Link></>}
      />

      <section className="hero-grid">
        <motion.article className="panel era-hero" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55 }}>
          <div className="hero-orbit orbit-a" /><div className="hero-orbit orbit-b" />
          <div className="hero-copy">
            <span className="eyebrow"><i className="live-dot" /> CURRENT ENTRY ERA</span>
            <h2>{member.era.charAt(0) + member.era.slice(1).toLowerCase()} <em>{member.era === 'STANDARD' ? '1.00×' : member.era === 'HALF' ? '0.50×' : '0.25×'}</em></h2>
            <p>This ID&apos;s era is the one it was created in on-chain. The exact current entry amount is read from the contract during Join review.</p>
            <div className="hero-price"><strong>On-chain</strong><span>BTCB<small>live quote at review</small></span></div>
            <div className="hero-actions"><Link to="/join" className="button light">Enter network <ArrowRight size={16} /></Link><Link to="/statistics" className="button glass">View era monitor</Link></div>
          </div>
          <div className="threshold-card">
            <div><span>Finalized indexer block</span><strong>{data.indexer.lastFinalizedBlock?.toLocaleString() ?? 'Awaiting sync'}</strong></div>
            <div className="threshold-meter"><motion.i initial={{ width: 0 }} animate={{ width: data.indexer.enabled ? '100%' : '0%' }} transition={{ duration: 1.1 }} /><b /></div>
            <div className="threshold-foot"><span>Indexer state</span><strong>{data.indexer.enabled ? (data.indexer.state ?? 'idle') : 'disabled'}</strong></div>
            <small><Clock3 size={13} /> {data.projectionNotice}</small>
          </div>
        </motion.article>

        <motion.article className="panel identity-card" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55, delay: 0.08 }}>
          <div className="identity-top"><div className="member-emblem"><span>BD</span><i /></div><StatusPill tone="green"><ShieldCheck size={12} /> Verified</StatusPill></div>
          <span className="muted-label">ACTIVE B-DYNAMIC ID</span>
          <h3>{member.id}</h3>
          <dl>
            <div><dt>Wallet</dt><dd>{shortenAddress(member.wallet, 6)}</dd></div>
            <div><dt>Sponsor</dt><dd>{member.sponsorId ?? 'Root'}</dd></div>
            <div><dt>Era</dt><dd><StatusPill tone="violet">{member.era}</StatusPill></dd></div>
            <div><dt>Joined</dt><dd>{formatDate(member.joinedAt).split(',').slice(0, 2).join(',')}</dd></div>
          </dl>
          <Link to={`/lookup?q=${member.id}`} className="text-link">Inspect public record <ExternalLink size={14} /></Link>
        </motion.article>
      </section>

      <ReferralCard memberId={member.id} />
      <DormancyReminder bdId={member.id} />
      <CdtCountryBanner bdId={member.id} idWallet={member.wallet} onChanged={data.refresh} />

      <MemberEarningsCard bdId={member.id} />

      <section className="panel audit-table"><div className="panel-heading"><div><span className="eyebrow">ALL SLOTS · SAME AS ADMIN ID BROWSER</span><h2>Your slot state</h2></div><Link to="/slots" className="text-link">Open the slots <ArrowRight size={14} /></Link></div>
        <SlotStateTable onChainSlots={data.slots.filter((slot) => slot.active).map((slot) => slot.number)} projectedSlots={data.projectedSlots} />
      </section>

      <IdReportsPanel bdId={member.id} />

      <section className="stats-grid five">
        <StatCard label="Total earned" value={formatToken(data.dashboardStats.totalEarned)} detail="Vault RewardPaid events (net, lifetime)" icon={BadgeDollarSign} tone="green" />
        <StatCard label="Claimable now" value={formatToken(data.dashboardStats.redeemable)} detail="Vault balance not yet withdrawn" icon={WalletCards} tone="amber" delay={0.05} />
        <StatCard label="Network members" value={data.dashboardStats.teamMembers.toLocaleString()} detail={<><TrendingUp size={12} /> Placed in the matrix on-chain</>} icon={Network} tone="cyan" delay={0.1} />
        <StatCard label="Active slots" value={`${activeSlots} / ${data.slots.length}`} detail={currentSlot ? `Activated on-chain · current Slot ${slotLabel(currentSlot.number)}` : 'No slot activated on-chain'} icon={Layers3} tone="violet" delay={0.15} />
        <StatCard label="Direct partners" value={data.dashboardStats.directMembers.toLocaleString()} detail="Placed directs on-chain" icon={Users} tone="pink" delay={0.2} />
      </section>

      <section className="dashboard-columns">
        <article className="panel chart-panel">
          <div className="panel-heading"><div><span className="eyebrow">NETWORK MOMENTUM</span><h2>Growth trajectory</h2></div><select aria-label="Chart range" defaultValue="14"><option value="14">Last 14 days</option><option value="30">Last 30 days</option></select></div>
          <Sparkline chartData={data.chartData} />
          <div className="chart-axis"><span>13 days ago</span><span>9 days ago</span><span>5 days ago</span><span>Today</span></div>
        </article>

        <article className="panel next-card">
          <div className="panel-heading"><div><span className="eyebrow">CURRENT MILESTONE</span><h2>{currentSlot ? currentSlot.label : 'No active slot'}</h2></div><span className="slot-gem"><Boxes /></span></div>
          <div className="radial-progress" style={{ '--progress': `${currentSlot?.progress ?? 0}%` } as React.CSSProperties}><div><strong>{Math.round(currentSlot?.progress ?? 0)}%</strong><span>complete</span></div></div>
          <ProgressBar value={currentSlot?.progress ?? 0} label={currentSlot ? `${currentSlot.placements} of 12 seats filled` : 'Awaiting activation'} />
          <p>Seats of the running cycle as the contract filled them (indexed SeatFilled events).</p>
          {currentSlot ? <Link to={`/slots/${currentSlot.number}`} className="button secondary full">View current matrix <ArrowRight size={15} /></Link> : null}
        </article>
      </section>

      <section className="dashboard-columns lower">
        <article className="panel activity-panel">
          <div className="panel-heading"><div><span className="eyebrow">CHAIN ACTIVITY</span><h2>Latest events</h2></div><Link to="/transactions" className="text-link">All transactions <ArrowRight size={14} /></Link></div>
          <div className="activity-list">
            {data.activity.slice(0, 4).map((record) => (
              <Link to={`/transactions?hash=${record.hash}`} key={record.id} className="activity-row">
                <span className={`activity-icon type-${record.type.toLowerCase()}`}><Activity size={16} /></span>
                <span className="activity-main"><strong>{record.type.charAt(0) + record.type.slice(1).toLowerCase()}</strong><small>{record.memberId} · {formatDate(record.timestamp)}</small></span>
                <span className="activity-value"><strong>{record.amount === '0' ? 'Utility issued' : formatToken(record.amount)}</strong><StatusPill tone={record.status === 'Pending' ? 'amber' : 'green'}>{record.status}</StatusPill></span>
              </Link>
            ))}
            {data.activity.length === 0 ? <p className="empty-copy">No finalized activity has been indexed for this ID.</p> : null}
          </div>
        </article>

        <div className="stack-column">
          <article className="panel token-mini">
            <span className="token-sheen" />
            <div className="token-symbol"><Crown /></div>
            <div><span className="eyebrow">UTILITY ASSET</span><h3>{data.tokens[0]?.tokenId ?? 'None issued'}</h3><p>{data.tokens[0] ? `${data.tokens[0].state} · lifecycle enforced on-chain` : 'No Primary Token minted on-chain yet'}</p></div>
            <Link to="/tokens"><ArrowRight /></Link>
          </article>
          <article className="panel rpc-card">
            <div className="panel-heading compact"><div><span className="eyebrow">MULTI-RPC</span><h3>Network resilience</h3></div><StatusPill tone={healthyRpcs === data.rpcProviders.length && healthyRpcs > 0 ? 'green' : 'amber'}>{healthyRpcs} / {data.rpcProviders.length} healthy</StatusPill></div>
            <div className="rpc-list">
              {data.rpcProviders.map((provider) => <div key={provider.id}><span className={`provider-dot ${provider.state}`} /><strong>{provider.name}</strong><em>{provider.latency === null ? 'n/a' : `${provider.latency} ms`}</em><small>priority {provider.priority}</small></div>)}
            </div>
          </article>
        </div>
      </section>

      <div className="integrity-note"><WalletCards size={17} /><span><strong>Privacy by design.</strong> No email, phone number, legal name or custodial key is stored by B-Dynamic.</span></div>
    </>
  );
}
